import { useEffect } from 'react';
import { configService } from '../../services/configService';
import { wasabiBackupService } from '../../services/wasabiBackupService';
import { structuredLogger } from '../../services/structuredLogger';
import { supabase } from '../../services/supabaseService';
import {
  mapDbConfigToState,
  mapLocalStorageToState,
  mapStorageConnectionToState
} from './settingsHelpers';
import type { SettingsState } from './useSettingsState';

export function useSettingsLoaders(state: SettingsState) {
  const { setters } = state;

  const loadBudgetConfig = async () => {
    try {
      const { data, error } = await supabase.rpc('get_active_budget_sync_config');

      if (error) {
        if (error.code === '42883' || error.message?.includes('does not exist')) {
          console.log('ℹ️ Budget sync RPC not available');
        } else {
          structuredLogger.warn('Could not fetch budget sync config', { error });
        }
        return;
      }

      if (data && data.length > 0) {
        const config = data[0];
        setters.setBudgetConfig({
          apiUrl: config.api_url || '',
          apiKey: '',
          isActive: config.is_active || false,
          lastTestedAt: config.last_tested_at || null,
          testStatus: config.test_status || 'never_tested',
          testMessage: config.test_message || ''
        });
      }
    } catch (error) {
      structuredLogger.error('Error loading budget sync config', { error });
    }
  };

  const loadOpenAIBillingInfo = async () => {
    try {
      console.log('💳 Cargando información de facturación...');
      const { openaiService } = await import('../../services/openaiService');

      const [billingInfo, usageHistory] = await Promise.all([
        openaiService.getBillingInfo(),
        openaiService.getUsageHistory()
      ]);

      setters.setOpenaiConfig(prev => ({
        ...prev,
        billingInfo,
        usageHistory
      }));

      console.log('✅ Información de facturación cargada');
    } catch (error) {
      console.error('Error loading billing info:', error);
    }
  };

  const loadConfiguration = async () => {
    try {
      console.log('📥 Cargando configuración...');

      const dbConfig = await configService.loadConfig(true);

      if (dbConfig) {
        console.log('✅ Configuración cargada desde base de datos');

        const mapped = mapDbConfigToState(dbConfig);

        if (mapped.general) setters.setGeneralConfig(mapped.general);
        if (mapped.firebase) setters.setFirebaseConfig(mapped.firebase);
        if (mapped.google) setters.setGoogleConfig(mapped.google);
        if (mapped.email) setters.setEmailConfig(prev => ({ ...prev, ...mapped.email }));
        if (mapped.openai) setters.setOpenaiConfig(mapped.openai);
        if (mapped.resend) setters.setResendConfig(mapped.resend);
      } else {
        console.log('⚠️ No se encontró configuración en BD, intentando localStorage como fallback...');

        const saved = localStorage.getItem('crm_configuration');
        if (saved) {
          const config = JSON.parse(saved);
          const mapped = mapLocalStorageToState(config);

          setters.setGeneralConfig(mapped.general);
          setters.setFirebaseConfig(mapped.firebase);
          setters.setGoogleConfig(mapped.google);
          setters.setEmailConfig(mapped.email);
          setters.setOpenaiConfig(mapped.openai);
          setters.setResendConfig(mapped.resend);
          setters.setGrafanaConfig(mapped.grafana);
        }
      }

      try {
        console.log('📦 Cargando configuración de Wasabi desde base de datos...');
        const storageConnection = await wasabiBackupService.getStorageConnection(null);

        if (storageConnection && storageConnection.service_type === 'wasabi') {
          console.log('✅ Configuración de Wasabi encontrada en base de datos');
          setters.setStorageConfig(mapStorageConnectionToState(storageConnection));
        } else {
          console.log('ℹ️ No se encontró configuración de Wasabi en base de datos');
        }
      } catch (error) {
        console.error('❌ Error cargando configuración de Wasabi:', error);
      }
    } catch (error) {
      console.error('Error loading configuration:', error);
    }
  };

  useEffect(() => {
    loadConfiguration();
    loadBudgetConfig();
  }, []);

  return {
    loadConfiguration,
    loadBudgetConfig,
    loadOpenAIBillingInfo
  };
}

export type UseSettingsLoaders = ReturnType<typeof useSettingsLoaders>;
